import { Dispatch, SetStateAction, useEffect } from "react"
import { makeStyles } from "tss-react/mui"
import { DialogContent, Typography } from "@mui/material"
import { LocalizedStrict } from "../../../../modules/localization/components/AppLocalized"
import { selectEffectiveUserGroup } from "../../../../modules/context/contextSelectors"
import { useAppSelector } from "../../../../modules/store/storeHooks"
import { UserType } from "../../../../modules/login/loginTypes"
import { StandardDialog } from "../../dialogs/StandardDialog"
import { StandardButton } from "../../buttons/StandardButton"
import { UserDetailsProps } from "./OrderOnBehalfFlow"
import { CompanySelect } from "./CompanySelect"
import { UserSelector } from "./UserSelector"

const useStyles = makeStyles()((theme) => ({
    dialogContent: {
        padding: "2.5rem",
        paddingTop: "1rem",
        display: "flex",
        gap: "2rem",
        flexDirection: "column",
        margin: "0 auto",
        color: theme.palette.tailwind.slate[950],
    },
    box: {
        padding: "1rem",
        borderRadius: ".5rem",
        background: theme.palette.tailwind.blue[200],
    },
}))

type OrderOnBehalfDialogProps = Readonly<{
    open: boolean
    onClose: () => void
    userId: number
    userType: UserType.MERCHANT | UserType.SERVICE_CENTER | UserType.COMPANY
    userDetailsState: UserDetailsProps
    setUserDetailsState: Dispatch<SetStateAction<UserDetailsProps>>
    handleConfirmSelection: () => void
}>

export function OrderOnBehalfDialog({
    open,
    onClose,
    userId,
    userType,
    handleConfirmSelection,
    userDetailsState,
    setUserDetailsState,
}: OrderOnBehalfDialogProps) {
    const { classes } = useStyles()
    const userGroupId = useAppSelector(selectEffectiveUserGroup)

    useEffect(() => {
        if (userType === UserType.COMPANY) {
            setUserDetailsState({
                companyId: userId.toString(),
                companyGroupId: userGroupId?.toString() ?? undefined,
                selectedUserId: undefined,
                selectedUserName: undefined,
            })
        }
    }, [userType, userId, userGroupId])

    return (
        <StandardDialog
            dialogTitle={
                <LocalizedStrict id="create-order-on-behalf-of-dialog">
                    <>Order on behalf of</>
                </LocalizedStrict>
            }
            open={open}
            onClose={onClose}
            maxWidthProp="sm"
            scroll="body"
        >
            <DialogContent className={classes.dialogContent}>
                <LocalizedStrict id="create-order-on-behalf-of-dialog-description">
                    <Typography variant="body1" className={classes.box}>
                        To create an order as an administrator, you must first select a user for whom you are placing
                        the order.
                    </Typography>
                </LocalizedStrict>
                {(userType === UserType.MERCHANT || userType === UserType.SERVICE_CENTER) && (
                    <CompanySelect
                        userDetails={userDetailsState}
                        setUserDetails={setUserDetailsState}
                        id={userId}
                        userType={userType}
                    />
                )}
                {userDetailsState.companyId && (
                    <UserSelector
                        id={userId}
                        userType={userType}
                        userDetails={userDetailsState}
                        setUserDetails={setUserDetailsState}
                    />
                )}

                {userDetailsState.selectedUserId && (
                    <LocalizedStrict id="create-order-on-behalf-of-dialog-button">
                        <StandardButton onClick={handleConfirmSelection} fullWidth={false}>
                            Continue
                        </StandardButton>
                    </LocalizedStrict>
                )}
            </DialogContent>
        </StandardDialog>
    )
}
